import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { Building2, MapPin, Award, Star, ArrowRight, Search, User, Leaf } from "lucide-react";

const hospitalImages = [
  "https://images.unsplash.com/photo-1688139885160-bb49f176ae2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob3NwaXRhbCUyMGJ1aWxkaW5nJTIwSW5kaWF8ZW58MHx8fHwxNzc2MjQ5Mzk0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1708451357500-abd1594730f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGJ1aWxkaW5nJTIwSW5kaWF8ZW58MHx8fHwxNzc2MjQ5Mzk0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1709722741348-3aecacb3f2c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGJ1aWxkaW5nJTIwSW5kaWF8ZW58MHx8fHwxNzc2MjQ5Mzk0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1687709644312-8c67bd4821c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGJ1aWxkaW5nJTIwSW5kaWF8ZW58MHx8fHwxNzc2MjQ5Mzk0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1615770891862-cf9c510272c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzYyNDgxMTl8MA&ixlib=rb-4.1.0&q=85",
  "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/5d6614bac40beeed91e374e34471c05cfd78e519778e0ce71bcb8693e85a8054.png",
];

const hospitals = [
  {
    name: "KIMS Hospital",
    location: "Thiruvananthapuram, Kerala",
    specialties: ["Cardiology", "Oncology", "Neurology", "Orthopedics"],
    accreditation: "NABH",
    beds: "850+",
    established: 2002,
    image: hospitalImages[0],
    desc: "Kerala Institute of Medical Sciences - a premier multi-specialty hospital offering advanced treatments with world-class infrastructure.",
  },
  {
    name: "Aster Medcity",
    location: "Kochi, Kerala",
    specialties: ["Cardiac Sciences", "Neurosciences", "Oncology", "Transplants"],
    accreditation: "JCI & NABH",
    beds: "670+",
    established: 2014,
    image: hospitalImages[1],
    desc: "One of the largest quaternary care hospitals in Kerala with cutting-edge technology and internationally trained specialists.",
  },
  {
    name: "Amrita Hospital",
    location: "Kochi, Kerala",
    specialties: ["Cardiac Surgery", "Neurosurgery", "Oncology", "Organ Transplants"],
    accreditation: "NABH",
    beds: "1200+",
    established: 1998,
    image: hospitalImages[2],
    desc: "Amrita Institute of Medical Sciences - a world-renowned teaching hospital known for complex medical procedures and compassionate care.",
  },
  {
    name: "Lakeshore Hospital",
    location: "Kochi, Kerala",
    specialties: ["Orthopedics", "Fertility", "Gastroenterology", "Cosmetic Surgery"],
    accreditation: "NABH",
    beds: "300+",
    established: 2001,
    image: hospitalImages[3],
    desc: "A premier multi-specialty hospital on the shores of Vembanad Lake, known for personalized patient care and medical excellence.",
  },
  {
    name: "Baby Memorial Hospital",
    location: "Kozhikode, Kerala",
    specialties: ["Cardiology", "Oncology", "Neurology", "Pediatrics"],
    accreditation: "NABH",
    beds: "600+",
    established: 1987,
    image: hospitalImages[4],
    desc: "A trusted healthcare institution in North Kerala, combining advanced medical technology with a patient-centric approach.",
  },
  {
    name: "Kottakkal Arya Vaidya Sala",
    location: "Malappuram, Kerala",
    specialties: ["Ayurveda", "Panchakarma", "Wellness", "Traditional Medicine"],
    accreditation: "NABH (Ayurveda)",
    beds: "150+",
    established: 1902,
    image: hospitalImages[5],
    desc: "The most renowned Ayurvedic institution in the world, offering authentic Panchakarma treatments and holistic wellness programs since 1902.",
  },
];

const doctors = [
  { name: "Dr. Ravi Krishnan", specialty: "Cardiology", hospital: "KIMS Hospital", exp: "25+ years", rating: 4.9 },
  { name: "Dr. Meera Nambiar", specialty: "Oncology", hospital: "Aster Medcity", exp: "20+ years", rating: 4.8 },
  { name: "Dr. Sunil Mathew", specialty: "Neurosurgery", hospital: "Amrita Hospital", exp: "22+ years", rating: 4.9 },
  { name: "Dr. Lakshmi Pillai", specialty: "Orthopedics", hospital: "Lakeshore Hospital", exp: "18+ years", rating: 4.7 },
  { name: "Dr. Arun Varier", specialty: "Ayurveda", hospital: "Kottakkal AVS", exp: "30+ years", rating: 4.9 },
  { name: "Dr. Priya Menon", specialty: "Fertility", hospital: "Lakeshore Hospital", exp: "15+ years", rating: 4.8 },
];

export default function HospitalsPage() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const allSpecialties = [...new Set(hospitals.flatMap((h) => h.specialties))].sort();

  const filteredHospitals = hospitals.filter((h) => {
    const matchesSearch = h.name.toLowerCase().includes(search.toLowerCase()) || h.location.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === "all" || h.specialties.some((s) => s.toLowerCase().includes(filter.toLowerCase()));
    return matchesSearch && matchesFilter;
  });

  return (
    <div data-testid="hospitals-page">
      <SEO title="Hospitals & Doctors" description="Kerala's premier hospitals - KIMS, Aster Medcity, Amrita Hospital, and Ayurvedic institutions. JCI & NABH accredited facilities with world-class specialists." path="/hospitals" keywords="Kerala hospitals, best hospitals Kerala, KIMS Thiruvananthapuram, Aster Medcity Kochi, Ayurveda hospital Kerala" />
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <span className="overline">HOSPITALS & DOCTORS</span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light text-[#03045e] mt-3 mb-4 tracking-tight">
              Kerala's <span className="font-medium text-[#00A4A9]">Premier Healthcare</span>
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed max-w-3xl mb-3">
              We partner with Kerala's top accredited hospitals and renowned Ayurvedic institutions to provide you the best possible care.
            </p>
            <div className="flex items-center gap-2 mb-10">
              <Badge className="bg-[#e0f2fe] text-[#00A4A9] hover:bg-[#e0f2fe] border-0">
                <Leaf className="w-3 h-3 mr-1" /> Ayurveda Specialists Available
              </Badge>
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search hospitals or locations..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-xl border-slate-300 focus:ring-[#00A4A9] focus:border-[#00A4A9]"
                  data-testid="hospital-search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={filter === "all" ? "default" : "outline"}
                  size="sm"
                  className={`rounded-full text-xs ${filter === "all" ? "bg-[#00A4A9] hover:bg-[#008084]" : ""}`}
                  onClick={() => setFilter("all")}
                  data-testid="filter-all"
                >
                  All
                </Button>
                {allSpecialties.slice(0, 6).map((s) => (
                  <Button
                    key={s}
                    variant={filter === s ? "default" : "outline"}
                    size="sm"
                    className={`rounded-full text-xs ${filter === s ? "bg-[#00A4A9] hover:bg-[#008084]" : ""}`}
                    onClick={() => setFilter(s)}
                    data-testid={`filter-${s.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {filteredHospitals.map((h, i) => (
              <FadeIn key={h.name} delay={i * 60}>
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`hospital-detail-${i}`}>
                  <div className="h-40 overflow-hidden">
                    <img src={h.image} alt={h.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-outfit text-lg font-medium text-[#03045e]">{h.name}</h3>
                      <Badge variant="outline" className="text-xs border-[#00A4A9]/30 text-[#00A4A9]">{h.accreditation}</Badge>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-slate-500 mb-3">
                      <MapPin className="w-3.5 h-3.5" /> {h.location}
                    </div>
                    <p className="text-sm text-slate-600 mb-4">{h.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {h.specialties.map((s) => (
                        <Badge key={s} variant="secondary" className="text-xs bg-[#F1F5F9] text-slate-600">{s}</Badge>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 border-t border-slate-100 pt-3">
                      <span>{h.beds} beds</span>
                      <span>Est. {h.established}</span>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Doctors */}
          <FadeIn>
            <div className="mb-12">
              <span className="overline">OUR SPECIALISTS</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                Leading Medical Experts
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {doctors.map((d, i) => (
              <FadeIn key={d.name} delay={i * 60}>
                <div className="bg-white border border-slate-200 rounded-2xl p-6 card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`doctor-card-${i}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-[#e0f2fe] flex items-center justify-center">
                      {d.specialty === "Ayurveda" ? <Leaf className="w-6 h-6 text-[#00A4A9]" /> : <User className="w-6 h-6 text-[#00A4A9]" />}
                    </div>
                    <div>
                      <h3 className="font-outfit font-medium text-[#03045e]">{d.name}</h3>
                      <p className="text-xs text-[#00A4A9] font-medium">{d.specialty}</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" /> {d.hospital}
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="w-3.5 h-3.5 text-slate-400" /> {d.exp} experience
                    </div>
                    <div className="flex items-center gap-2">
                      <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /> {d.rating} / 5.0
                    </div>
                  </div>
                  <Link to="/contact" className="block mt-4" data-testid={`doctor-inquiry-${i}`}>
                    <Button variant="outline" size="sm" className="w-full rounded-full text-[#00A4A9] border-[#00A4A9]/30 hover:bg-[#00A4A9] hover:text-white">
                      Book Consultation <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
